/*
 * Header navigation behaviour.
 *
 * <header-nav> enhances the desktop navigation's <details> menus: opens them
 * on hover, keeps only one open, closes on Escape / outside click / focus
 * leaving, and moves top-level links that don't fit on one row into the
 * trailing "more" panel. The panels are in the page (snippets/mega-menu.liquid).
 *
 * <menu-drawer> opens and closes the mobile navigation dialog, whose full
 * menu is in the page (snippets/menu-drawer-links.liquid).
 *
 * <dropdown-disclosure> closes a small <details> dropdown (such as the
 * language selector) on outside click and Escape, and carries the current
 * query string over to language links.
 *
 * <overflow-row> keeps a list on a single line: items that wrap onto a second
 * row are hidden, so the row never grows and hidden links leave the tab
 * order. CSS clips the second row until this runs.
 *
 * <sticky-header> is the header itself. CSS makes its container sticky (Theme
 * settings > Navigation > Sticky header); in `scroll_up` mode this hides it
 * while scrolling down and shows it again on the way up. It publishes the
 * visible height as `--sticky-header-height` for sticky content below it.
 */

// A panel opens the moment the mouse reaches its item, and switches the moment it reaches another
// (Horizon also opens at 0 ms, then animates the panel in over 360 ms; ours takes 0.2 s, header.css).
// Closing waits a little, so a pointer slipping off the panel for a moment doesn't close it, then
// fades the panel out (data-closing, header.css) before it's closed.
const HOVER_CLOSE_DELAY = 200;
const CLOSE_MOTION = 120;

class HeaderNav extends HTMLElement {
  #abort;
  #resizeObserver;
  #timers = new Map();
  #hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  #warmed = false;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.list = this.querySelector('[data-nav-list]');
    this.items = [...this.querySelectorAll('[data-nav-item]')];
    this.more = this.querySelector('[data-nav-more]');
    this.moreItems = [...this.querySelectorAll('[data-more-item]')];
    this.more?.addEventListener('click', this.#onMoreClick, { signal });

    // `toggle` doesn't bubble, so listen in the capture phase.
    this.addEventListener('toggle', this.#onToggle, { capture: true, signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });
    document.addEventListener('click', this.#onDocumentClick, { signal });

    // The first time a mouse reaches the bar, load every panel's thumbnails while the browser is idle,
    // so any panel opens complete. Touch-only visitors and visitors who never use the menu load none.
    this.list?.addEventListener(
      'pointerenter',
      (event) => {
        if (this.#warmed || event.pointerType !== 'mouse') return;
        this.#warmed = true;
        (window.requestIdleCallback ?? setTimeout)(() => this.#menus().forEach((details) => this.#warmImages(details)));
      },
      { signal }
    );

    for (const details of this.#menus()) {
      details.addEventListener('mouseenter', () => this.#hover(details, true), { signal });
      details.addEventListener('mouseleave', () => this.#hover(details, false), { signal });
      details.addEventListener('focusin', () => this.#warmImages(details), { signal });
      details.querySelector('summary').addEventListener('click', (event) => this.#onSummaryClick(event, details), {
        signal,
      });
    }

    this.#resizeObserver = new ResizeObserver(() => this.#updateOverflow());
    this.#resizeObserver.observe(this);
    document.fonts?.ready.then(() => this.#updateOverflow());
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#resizeObserver.disconnect();
    this.#timers.forEach(clearTimeout);
  }

  #menus() {
    return this.querySelectorAll(':scope details');
  }

  #closeAll(except) {
    for (const details of this.#menus()) {
      if (details !== except) details.open = false;
    }
  }

  #hover(details, entering) {
    if (!this.#hoverQuery.matches) return;

    clearTimeout(this.#timers.get(details));

    if (entering) {
      // Back over a panel that is fading out: it stays.
      details.removeAttribute('data-closing');
      if (details.open) return;
      this.#warmImages(details);
      // Straight from another open panel: swap without the opening motion (header.css), no flicker.
      details.toggleAttribute('data-instant', [...this.#menus()].some((menu) => menu.open && menu !== details));
      details.open = true;
    } else {
      this.#timers.set(details, setTimeout(() => this.#closeWithMotion(details), HOVER_CLOSE_DELAY));
    }
  }

  // Fades the panel out (header.css, [data-closing]) and then closes it; at once with reduced motion.
  // Coming back over it meanwhile keeps it open (#hover), and opening another closes it at once (#closeAll).
  #closeWithMotion(details) {
    if (!details.open) return;
    if (this.#reducedMotion.matches) {
      details.open = false;
      return;
    }
    details.setAttribute('data-closing', '');
    this.#timers.set(
      details,
      setTimeout(() => {
        if (!details.hasAttribute('data-closing')) return;
        details.removeAttribute('data-closing');
        details.open = false;
      }, CLOSE_MOTION)
    );
  }

  // A closed <details> isn't rendered, so its lazy images wait until the menu opens. Focusing an item
  // starts loading them before it opens; with a mouse, every panel's images load when it first reaches the bar.
  #warmImages(details) {
    for (const image of details.querySelectorAll('img[loading="lazy"]')) image.loading = 'eager';
  }

  // With a mouse the menu opens on hover, so a click on the category goes to it (like a link).
  // Keyboard (detail 0) and touch keep the native toggle, which opens the panel.
  #onSummaryClick(event, details) {
    if (event.detail === 0 || !this.#hoverQuery.matches) return;

    event.preventDefault();
    const url = details.querySelector('summary').dataset.url;
    if (url) window.location.assign(url);
  }

  #onToggle = (event) => {
    if (!(event.target instanceof HTMLDetailsElement)) return;
    if (event.target.open) {
      this.#closeAll(event.target);
    } else {
      // A keyboard or touch open later gets the opening motion again; a fade cut short is over.
      event.target.removeAttribute('data-instant');
      event.target.removeAttribute('data-closing');
      event.target.querySelectorAll('[data-open]').forEach((item) => item.removeAttribute('data-open'));
    }
  };

  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;

    const details = this.querySelector('details[open]');
    if (!details) return;

    details.open = false;
    details.querySelector('summary').focus();
  };

  // Close when keyboard focus moves outside an open menu; clicks are handled separately.
  #onFocusOut = (event) => {
    const details = event.target.closest('details');
    if (details?.open && event.relatedTarget && !details.contains(event.relatedTarget)) {
      details.open = false;
    }
  };

  #onDocumentClick = (event) => {
    if (!this.contains(event.target)) this.#closeAll();
  };

  #updateOverflow() {
    if (!this.list || !this.more) return;

    this.items.forEach((item) => (item.hidden = false));
    this.moreItems.forEach((item) => (item.hidden = true));
    this.more.hidden = true;

    const listRect = this.list.getBoundingClientRect();
    if (this.list.scrollWidth <= Math.ceil(listRect.width)) return;

    this.#closeAll();
    this.more.hidden = false;

    const gap = parseFloat(getComputedStyle(this.list).columnGap) || 0;
    const limit = listRect.width - this.more.offsetWidth - gap;
    const firstHidden = this.items.findIndex((item) => item.getBoundingClientRect().right - listRect.left > limit);

    if (firstHidden === -1) {
      this.more.hidden = true;
      return;
    }

    this.items.slice(firstHidden).forEach((item) => (item.hidden = true));
    this.moreItems.slice(firstHidden).forEach((item) => (item.hidden = false));

    // The dropdown hangs under the pill: its distance from the bar's right edge (header.css, --more-right).
    const pill = this.more.querySelector('summary').getBoundingClientRect();
    this.style.setProperty('--more-right', `${Math.max(0, this.getBoundingClientRect().right - pill.right)}px`);
  }

  // Touch screens have no hover: the first tap on a "more" link with subcategories opens them beside the
  // list, the next one follows the link.
  #onMoreClick = (event) => {
    const title = event.target.closest('.more-menu__title');
    const item = title?.closest('.more-menu__item');
    if (!item?.querySelector('.more-menu__sub') || this.#hoverQuery.matches || item.hasAttribute('data-open')) return;

    event.preventDefault();
    this.moreItems.forEach((other) => other.toggleAttribute('data-open', other === item));
  };
}

class MenuDrawer extends HTMLElement {
  #abort;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.dialog = this.querySelector('dialog');
    this.opener = document.querySelector(`[aria-controls="${this.dialog.id}"]`);

    this.opener?.addEventListener('click', () => this.open(), { signal });
    this.querySelectorAll('[data-menu-drawer-close]').forEach((button) =>
      button.addEventListener('click', () => this.close(), { signal })
    );

    // Clicks on the backdrop target the dialog itself.
    this.dialog.addEventListener('click', (event) => event.target === this.dialog && this.close(), { signal });
    this.dialog.addEventListener('close', () => this.opener?.setAttribute('aria-expanded', 'false'), { signal });

    window.matchMedia('(min-width: 990px)').addEventListener('change', (event) => event.matches && this.close(), {
      signal,
    });
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  open() {
    this.dialog.showModal();
    this.opener?.setAttribute('aria-expanded', 'true');
  }

  close() {
    if (this.dialog.open) this.dialog.close();
  }
}

class DropdownDisclosure extends HTMLElement {
  #abort;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.details = this.querySelector('details');

    // Language links keep the current query (such as ?variant=), which Liquid can't read.
    this.addEventListener(
      'click',
      (event) => {
        const link = event.target.closest('a[hreflang]');
        if (link) link.search = window.location.search;
      },
      { signal }
    );

    document.addEventListener('click', (event) => !this.contains(event.target) && (this.details.open = false), {
      signal,
    });
    this.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape' || !this.details.open) return;
        this.details.open = false;
        this.details.querySelector('summary').focus();
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#abort.abort();
  }
}

class OverflowRow extends HTMLElement {
  #resizeObserver;

  connectedCallback() {
    this.list = this.querySelector('ul');
    if (!this.list) return;

    // Watch the parent row, whose size doesn't depend on how many items are shown here.
    this.#resizeObserver = new ResizeObserver(() => this.#update());
    this.#resizeObserver.observe(this.parentElement);
    document.fonts?.ready.then(() => this.#update());
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
  }

  #update() {
    const items = [...this.list.children];
    items.forEach((item) => (item.hidden = false));

    // Read every position before hiding anything, so layout is computed once.
    const tops = items.map((item) => item.offsetTop);
    items.forEach((item, index) => (item.hidden = tops[index] > tops[0]));
  }
}

// Scroll movements smaller than this don't change the header's direction state.
const SCROLL_DIRECTION_THRESHOLD = 8;

class StickyHeader extends HTMLElement {
  #abort;
  #resizeObserver;
  #frame;
  #lastY = 0;

  connectedCallback() {
    if (this.dataset.sticky === 'none') return;

    this.#abort = new AbortController();
    this.utility = this.querySelector('.site-header__utility');
    this.#resizeObserver = new ResizeObserver(() => this.#publishHeight());
    this.#resizeObserver.observe(this);

    if (this.dataset.sticky === 'scroll_up') {
      this.#lastY = window.scrollY;
      window.addEventListener('scroll', this.#onScroll, { passive: true, signal: this.#abort.signal });
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  // The part that stays on screen when stuck: the utility bar scrolls away, unless the Header section
  // keeps it in view ([data-bar-sticky]).
  #publishHeight() {
    const barScrollsAway = !this.hasAttribute('data-bar-sticky');
    const height = this.hasAttribute('data-hidden')
      ? 0
      : this.offsetHeight - (barScrollsAway ? (this.utility?.offsetHeight ?? 0) : 0);
    document.documentElement.style.setProperty('--sticky-header-height', `${height}px`);
  }

  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      const y = window.scrollY;
      const delta = y - this.#lastY;
      if (Math.abs(delta) < SCROLL_DIRECTION_THRESHOLD) return;
      this.#lastY = y;

      // Hide once scrolled past the header, and never while one of its menus, drawers, or search is open.
      const open = this.querySelector('details[open], dialog[open], predictive-search[open]');
      const hide = delta > 0 && y > this.offsetHeight && !open;
      if (hide === this.hasAttribute('data-hidden')) return;
      this.toggleAttribute('data-hidden', hide);
      this.#publishHeight();
    });
  };
}

if (!customElements.get('sticky-header')) customElements.define('sticky-header', StickyHeader);
if (!customElements.get('header-nav')) customElements.define('header-nav', HeaderNav);
if (!customElements.get('overflow-row')) customElements.define('overflow-row', OverflowRow);
if (!customElements.get('menu-drawer')) customElements.define('menu-drawer', MenuDrawer);
if (!customElements.get('dropdown-disclosure')) customElements.define('dropdown-disclosure', DropdownDisclosure);
